"use client";

import React, { useState, useEffect, useRef } from 'react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ClipboardCheck, ArrowRightLeft, Cpu, ChartLine, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Assessment",
  description: "We analyze your current infrastructure, identifying bottlenecks and opportunities for AI integration.",
  icon: ClipboardCheck,
  number: "01"
}, {
  title: "Migration",
  description: "Seamlessly moving your data and workloads to scalable cloud environments with zero downtime.",
  icon: ArrowRightLeft,
  number: "02"
}, {
  title: "Automation",
  description: "Implementing intelligent agents and workflows to automate repetitive tasks and enhance efficiency.",
  icon: Cpu,
  number: "03"
}, {
  title: "Optimization",
  description: "Continuous monitoring and refining of your systems to ensure peak performance and cost-effectiveness.",
  icon: ChartLine,
  number: "04"
}];
export default function YourInnovationRoadmap() {
  const containerRef = useRef<HTMLElement>(null);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [progress, setProgress] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      if (!containerRef.current) return;
      const windowHeight = window.innerHeight;
      const rect = containerRef.current.getBoundingClientRect();
      const containerTop = rect.top;
      const containerHeight = rect.height;
      const startPoint = windowHeight / 2;
      const scrollDist = startPoint - containerTop;
      const rawProgress = scrollDist / containerHeight * 100;
      const clampedProgress = Math.max(0, Math.min(100, rawProgress));
      setProgress(clampedProgress);
      let currentActive = 0;
      stepRefs.current.forEach((step, index) => {
        if (!step) return;
        const stepRect = step.getBoundingClientRect();
        if (stepRect.top < windowHeight * 0.75) {
          currentActive = index;
        }
      });
      if (rect.top < windowHeight && rect.bottom > 0) {
        setActiveStep(currentActive);
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return <section id="your-innovation-roadmap" ref={containerRef} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
            Your Innovation <span className="text-primary">Roadmap</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            A strategic, step-by-step journey to transform your business with AI and Cloud technologies.
          </p>
        </div>

        <div className="relative max-w-5xl mx-auto">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-1 bg-border/30 -translate-x-1/2 rounded-full overflow-hidden">
            {}
            <div className="w-full bg-primary shadow-[0_0_20px_2px_var(--color-primary)] transition-all duration-100 ease-linear rounded-full" style={{
            height: `${progress}%`
          }} />
          </div>

          {}
          <div className="space-y-24 md:space-y-32">
            {steps.map((step, index) => {
            const isActive = index === activeStep;
            const isEven = index % 2 === 0;
            return <div key={index} data-index={index} ref={el => {
              stepRefs.current[index] = el;
            }} className={cn("relative grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center transition-opacity duration-500", isActive ? "opacity-100" : "opacity-40")}>
                  {}
                  <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-background border-2 border-primary z-20 shadow-[0_0_10px_var(--color-primary)]">
                    <div className={cn("absolute inset-0 rounded-full bg-primary transition-all duration-500", isActive ? "opacity-100 scale-150 animate-pulse" : "opacity-0 scale-100")} />
                  </div>

                  {}
                  <div className={cn("pl-20 md:pl-0", isEven ? "md:text-right md:pr-12" : "md:col-start-2 md:pl-12")}>
                    <div className="relative">
                      {}
                      <span className={cn("absolute -top-12 md:-top-16 text-8xl md:text-9xl font-black text-primary/10 select-none -z-10 transition-all duration-700", isEven ? "left-0 md:right-0" : "left-0", isActive ? "scale-110 text-primary/15" : "scale-100")}>
                        {step.number}
                      </span>

                      <Card className={cn("relative overflow-hidden border transition-all duration-500 backdrop-blur-sm", isActive ? "border-primary/50 bg-card/40 shadow-[0_0_30px_-10px_var(--color-primary)]" : "border-border/50 bg-card/20 shadow-none")}>
                        <CardContent className="p-6 md:p-8">
                          <div className={cn("flex items-center gap-4 mb-4", isEven ? "md:flex-row-reverse" : "flex-row")}>
                            <div className={cn("p-3 rounded-xl transition-colors duration-500", isActive ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground")}>
                              <step.icon className="w-6 h-6" />
                            </div>
                            <h3 className="text-2xl font-bold text-foreground">{step.title}</h3>
                          </div>
                          <p className="text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {}
                  <div className="hidden md:block" />
                </div>;
          })}
          </div>
        </div>

        <div className="mt-24 text-center">
          <Link to="/ConnectPage">
            <Button size="lg" className="text-lg px-8 py-6 rounded-full shadow-[0_0_20px_-5px_var(--color-primary)] hover:shadow-[0_0_30px_-5px_var(--color-primary)] transition-all">
              Start Your Journey <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}